/**
 * AttestationsPopover Component
 *
 * Displays provider attestations panel following ADR-012.
 * Uses inline editing (no nested popovers) for reliability.
 */

import React, { useCallback, useEffect, useMemo, useRef,useState } from 'react';
import { CPTCodePicker } from '@components/ui/forms/CPTCodePicker';
import { ICD10CodePicker } from '@components/ui/forms/ICD10CodePicker';
import { useUpdateEncounterMutation } from '@features/encounters';
import type { EncounterAttestations } from '@features/encounters/types/attestations';
import {
  AccessTime as TimeIcon,
  AttachMoney as BillingIcon,
  Check as CheckIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  LocalPharmacy as PdmpIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  RadioButtonUnchecked as UncheckedIcon,
  VideoCall as VideoIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  alpha,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import type { EncounterResponse } from '@store/generated/encounters';
import type { NoteResponse } from '@store/generated/notes';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';

export interface AttestationsPopoverProps {
  noteId: string;
  encounterId?: string;
  encounterData?: EncounterResponse;
  noteData?: NoteResponse;
  editable?: boolean;
  /**
   * Callback when user clicks Sign/Complete Note button
   */
  onSignNote?: () => void;
  /**
   * Whether the sign/complete action is in progress
   */
  isSigning?: boolean;
  /**
   * Whether note signing feature is enabled
   */
  hasNoteSigning?: boolean;
  /**
   * Whether note has a patient assigned
   */
  hasPatient?: boolean;
}

type AttestationStatus = 'complete' | 'warning' | 'blocked';
type ExpandedSection = 'modality' | 'time' | 'billing' | null;

const getModalityIcon = (modality: string) => {
  switch (modality) {
    case 'video':
      return <VideoIcon sx={{ fontSize: 16 }} />;
    case 'audio':
      return <PhoneIcon sx={{ fontSize: 16 }} />;
    case 'in_person':
      return <PersonIcon sx={{ fontSize: 16 }} />;
    default:
      return <PersonIcon sx={{ fontSize: 16 }} />;
  }
};

const getModalityLabel = (modality: string) => {
  switch (modality) {
    case 'video':
      return 'Video';
    case 'audio':
      return 'Audio';
    case 'in_person':
      return 'In-person';
    default:
      return modality;
  }
};

export const AttestationsPopover: React.FC<AttestationsPopoverProps> = ({
  noteId,
  encounterId,
  encounterData,
  noteData,
  editable = true,
  onSignNote,
  isSigning = false,
  hasNoteSigning = false,
  hasPatient = false,
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [updateEncounter] = useUpdateEncounterMutation();

  // Which section is expanded for editing
  const [expandedSection, setExpandedSection] = useState<ExpandedSection>(null);

  // Derive attestations object from encounterData
  const attestations = useMemo(
    () => (encounterData?.attestations ?? {}) as EncounterAttestations,
    [encounterData?.attestations],
  );

  // Derive values directly from encounterData (single source of truth)
  const modality = (encounterData?.modality ?? 'video');
  const placeOfService = (encounterData?.place_of_service ?? '10') as '11' | '10' | '02';
  const icd10Codes = encounterData?.icd10_codes ?? [];
  const telehealthConsent = attestations.telehealth_consent ?? true;
  const locationConfirmed = attestations.location_confirmed ?? true;
  const pdmpReviewed = attestations.pdmp_reviewed ?? false;
  const noteReviewed = attestations.note_reviewed_and_accurate ?? true;
  const psychotherapyMinutes = attestations.psychotherapy_minutes?.toString() ?? '';

  // Local state for CPT codes picker (keeps debounced save pattern)
  const [cptCodes, setCptCodes] = useState<string[]>([]);
  // Ref to track if CPT codes were changed by user (vs. API response)
  const cptUserChangedRef = useRef(false);
  // Ref to access latest cptCodes in callbacks without needing it in dependencies
  const cptCodesRef = useRef<string[]>([]);
  cptCodesRef.current = cptCodes;

  // Local state for time editing (buffered input while user types)
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedStartTime, setSelectedStartTime] = useState<string>('');
  const [selectedEndTime, setSelectedEndTime] = useState<string>('');
  const [totalTimeMinutes, setTotalTimeMinutes] = useState<string>('');

  // Helper to compare arrays
  const arraysEqual = (a: string[], b: string[]) => {
    if (a.length !== b.length) return false;
    return a.every((val, idx) => val === b[idx]);
  };

  // Initialize CPT codes from encounter data (keeps local state for debounced save)
  useEffect(() => {
    if (encounterData) {
      // Only update local state if values actually differ (prevents infinite loop)
      const newCptCodes = encounterData.cpt_codes || [];
      if (!arraysEqual(newCptCodes, cptCodesRef.current)) {
        // Only reset user-changed flag if we're not in the middle of user editing
        if (!cptUserChangedRef.current) {
          setCptCodes(newCptCodes);
        }
      }

      // Calculate duration with fallback chain (matching NoteMetadata pattern)
      if (encounterData.start_time && encounterData.end_time) {
        const start = new Date(encounterData.start_time).getTime();
        const end = new Date(encounterData.end_time).getTime();
        const minutes = Math.floor((end - start) / (1000 * 60));
        setTotalTimeMinutes(String(minutes));
      } else if (encounterData.appointment?.start_time && encounterData.appointment?.duration) {
        // Calculate end_time from start_time + duration (duration is in minutes)
        const start = new Date(encounterData.appointment.start_time).getTime();
        const end = start + (encounterData.appointment.duration * 60 * 1000);
        const minutes = Math.floor((end - start) / (1000 * 60));
        setTotalTimeMinutes(String(minutes));
      } else if (encounterData.appointment?.duration) {
        setTotalTimeMinutes(String(encounterData.appointment.duration));
      }
    }
  }, [encounterData]);

  // Get start/end times with fallback chain (matching NoteMetadata pattern)
  // Must be defined before useEffects that use them
  const startTime = useMemo(() => {
    if (encounterData?.start_time) {
      return new Date(encounterData.start_time);
    } else if (encounterData?.appointment?.start_time) {
      return new Date(encounterData.appointment.start_time);
    }
    return null;
  }, [encounterData]);

  const endTime = useMemo(() => {
    // First try direct end_time fields
    if (encounterData?.end_time) {
      return new Date(encounterData.end_time);
    } else if (encounterData?.appointment?.start_time && encounterData?.appointment?.duration) {
      // Calculate end_time from start_time + duration (duration is in minutes)
      const start = new Date(encounterData.appointment.start_time).getTime();
      return new Date(start + (encounterData.appointment.duration * 60 * 1000));
    }
    
    // Otherwise calculate from start time + duration
    const start = startTime;
    if (start) {
      // Try to get duration from totalTimeMinutes (user-set value)
      if (totalTimeMinutes) {
        const minutes = parseInt(totalTimeMinutes, 10);
        if (minutes > 0) {
          return new Date(start.getTime() + minutes * 60 * 1000);
        }
      }
      // Or from appointment duration
      else if (encounterData?.appointment?.duration) {
        const minutes = encounterData.appointment.duration;
        return new Date(start.getTime() + minutes * 60 * 1000);
      }
    }
    
    return null;
  }, [encounterData, startTime, totalTimeMinutes]);

  // Toggle section expansion
  const toggleSection = (section: ExpandedSection) => {
    if (!editable) return;
    setExpandedSection((prev) => (prev === section ? null : section));
  };

  // Mutation handlers for direct encounter updates
  const handleAttestationChange = useCallback(
    async (field: keyof EncounterAttestations, value: unknown) => {
      if (!encounterId) return;
      try {
        await updateEncounter({
          encounterId,
          encounterUpdate: {
            attestations: { ...attestations, [field]: value },
          },
        }).unwrap();
      } catch (error) {
        console.error('Failed to update attestation:', error);
        enqueueSnackbar('Failed to update attestation', { variant: 'error' });
      }
    },
    [encounterId, attestations, updateEncounter, enqueueSnackbar],
  );

  const handleModalityChange = useCallback(
    async (newModality: 'in_person' | 'video' | 'audio') => {
      if (!encounterId) return;
      const pos = newModality === 'in_person' ? '11' : newModality === 'video' ? '10' : '02';
      try {
        await updateEncounter({
          encounterId,
          encounterUpdate: { modality: newModality, place_of_service: pos },
        }).unwrap();
      } catch (error) {
        console.error('Failed to update modality:', error);
        enqueueSnackbar('Failed to update modality', { variant: 'error' });
      }
    },
    [encounterId, updateEncounter, enqueueSnackbar],
  );

  const handleIcd10Change = useCallback(
    async (codes: string[]) => {
      if (!encounterId) return;
      try {
        await updateEncounter({
          encounterId,
          encounterUpdate: { icd10_codes: codes },
        }).unwrap();
      } catch (error) {
        console.error('Failed to update ICD-10 codes:', error);
        enqueueSnackbar('Failed to update ICD-10 codes', { variant: 'error' });
      }
    },
    [encounterId, updateEncounter, enqueueSnackbar],
  );

  // Initialize time editor fields when time section expands
  useEffect(() => {
    if (expandedSection === 'time' && startTime) {
      // Format date as YYYY-MM-DD for date input
      const dateStr = format(startTime, 'yyyy-MM-dd');
      setSelectedDate(dateStr);
      
      // Format time as HH:mm for time input
      const timeStr = format(startTime, 'HH:mm');
      setSelectedStartTime(timeStr);
      
      // Format end time if available
      if (endTime) {
        const endTimeStr = format(endTime, 'HH:mm');
        setSelectedEndTime(endTimeStr);
      } else {
        setSelectedEndTime('');
      }
    }
  }, [expandedSection, startTime, endTime]);

  // Auto-calculate duration when end time changes
  useEffect(() => {
    if (selectedDate && selectedStartTime && selectedEndTime && expandedSection === 'time') {
      const start = new Date(`${selectedDate}T${selectedStartTime}`);
      let end = new Date(`${selectedDate}T${selectedEndTime}`);
      // Handle case where end time is next day
      if (end < start) {
        end = new Date(end.getTime() + 24 * 60 * 60 * 1000);
      }
      const minutes = Math.floor((end.getTime() - start.getTime()) / (1000 * 60));
      if (minutes > 0) {
        setTotalTimeMinutes(String(minutes));
      }
    }
  }, [selectedDate, selectedStartTime, selectedEndTime, expandedSection]);

  // Quick toggle for PDMP
  const handlePdmpQuickToggle = useCallback(async () => {
    if (!editable || !encounterId) return;
    const newValue = !pdmpReviewed;
    enqueueSnackbar(newValue ? 'PDMP reviewed ✓' : 'PDMP unchecked', {
      variant: newValue ? 'success' : 'default',
      autoHideDuration: 1500,
    });
    await handleAttestationChange('pdmp_reviewed', newValue);
  }, [editable, encounterId, pdmpReviewed, enqueueSnackbar, handleAttestationChange]);

  // Save handlers (autosave)
  const handleSaveCptCodes = useCallback(() => {
    if (!encounterId) {
      return;
    }
    // Use ref to get latest cptCodes without needing it in dependencies
    const codes = cptCodesRef.current.map((c) => c.trim()).filter((c) => c !== '');
    updateEncounter({ encounterId, encounterUpdate: { cpt_codes: codes } })
      .unwrap()
      .then(() => {
        // Reset user-changed flag after successful save
        cptUserChangedRef.current = false;
      })
      .catch(() => enqueueSnackbar('Failed to update CPT codes', { variant: 'error' }));
  }, [encounterId, updateEncounter, enqueueSnackbar]);

  const handleSaveTime = useCallback(() => {
    if (!encounterId || !selectedDate || !selectedStartTime) {
      return;
    }

    // Combine date and start time
    const startDateTime = new Date(`${selectedDate}T${selectedStartTime}`);
    
    // Calculate end time - prefer explicit end time, otherwise use duration
    let endDateTime: Date | null = null;
    if (selectedEndTime) {
      endDateTime = new Date(`${selectedDate}T${selectedEndTime}`);
      // Handle case where end time is next day
      if (endDateTime < startDateTime) {
        endDateTime = new Date(endDateTime.getTime() + 24 * 60 * 60 * 1000);
      }
    } else if (totalTimeMinutes) {
      const minutes = parseInt(totalTimeMinutes, 10) || 0;
      if (minutes > 0) {
        endDateTime = new Date(startDateTime.getTime() + minutes * 60 * 1000);
      }
    }

    updateEncounter({
      encounterId,
      encounterUpdate: {
        start_time: startDateTime.toISOString(),
        end_time: endDateTime ? endDateTime.toISOString() : null,
      },
    })
      .unwrap()
      .catch(() => enqueueSnackbar('Failed to update time', { variant: 'error' }));
  }, [encounterId, selectedDate, selectedStartTime, selectedEndTime, totalTimeMinutes, updateEncounter, enqueueSnackbar]);

  // Autosave time when values change (debounced)
  const timeSaveTimeoutRef = useRef<NodeJS.Timeout>();
  useEffect(() => {
    if (expandedSection === 'time' && selectedDate && selectedStartTime) {
      // Clear previous timeout
      if (timeSaveTimeoutRef.current) {
        clearTimeout(timeSaveTimeoutRef.current);
      }
      // Debounce save by 500ms
      timeSaveTimeoutRef.current = setTimeout(() => {
        handleSaveTime();
      }, 500);
      return () => {
        if (timeSaveTimeoutRef.current) {
          clearTimeout(timeSaveTimeoutRef.current);
        }
      };
    }
  }, [selectedDate, selectedStartTime, selectedEndTime, totalTimeMinutes, expandedSection, handleSaveTime]);

  // Autosave CPT codes when they change (debounced)
  // Only save when user actually changed the codes (not on API response)
  const cptSaveTimeoutRef = useRef<NodeJS.Timeout>();
  useEffect(() => {
    // Only trigger save if user explicitly changed the codes
    if (expandedSection === 'billing' && encounterId && cptUserChangedRef.current) {
      // Clear previous timeout
      if (cptSaveTimeoutRef.current) {
        clearTimeout(cptSaveTimeoutRef.current);
      }
      // Debounce save by 500ms
      cptSaveTimeoutRef.current = setTimeout(() => {
        handleSaveCptCodes();
      }, 500);
      return () => {
        if (cptSaveTimeoutRef.current) {
          clearTimeout(cptSaveTimeoutRef.current);
        }
      };
    }
  }, [cptCodes, expandedSection, encounterId, handleSaveCptCodes]);

  // Calculate duration display - prefer totalTimeMinutes, but calculate from start/end if available
  const durationDisplay = useMemo(() => {
    let minutes: number | null = null;
    
    // First try to use totalTimeMinutes if set
    if (totalTimeMinutes) {
      minutes = parseInt(totalTimeMinutes, 10);
    }
    // Otherwise calculate from start/end times if both are available
    else if (startTime && endTime) {
      const start = startTime.getTime();
      const end = endTime.getTime();
      minutes = Math.floor((end - start) / (1000 * 60));
    }
    
    if (minutes && minutes > 0) {
      if (minutes >= 60) {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours}h${mins > 0 ? ` ${mins}m` : ''}`;
      }
      return `${minutes}m`;
    }
    return null;
  }, [totalTimeMinutes, startTime, endTime]);

  // Section status indicators
  const getSectionStatus = (section: 'modality' | 'time' | 'billing' | 'attestation' | 'pdmp'): AttestationStatus => {
    switch (section) {
      case 'modality':
        return modality !== 'in_person' && (!telehealthConsent || !locationConfirmed) ? 'warning' : 'complete';
      case 'time':
        return totalTimeMinutes ? 'complete' : 'warning';
      case 'billing':
        return cptCodes.length > 0 && icd10Codes.length > 0 ? 'complete' : 'warning';
      case 'attestation':
        return noteReviewed ? 'complete' : 'warning';
      case 'pdmp':
        return pdmpReviewed ? 'complete' : 'warning';
    }
  };

  const getStatusColor = (status: AttestationStatus) => {
    switch (status) {
      case 'complete':
        return theme.palette.success.main;
      case 'warning':
        return theme.palette.warning.main;
      case 'blocked':
        return theme.palette.warning.main;
    }
  };

  const getStatusIcon = (status: AttestationStatus) => {
    switch (status) {
      case 'complete':
        return <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />;
      case 'warning':
        return <WarningIcon sx={{ fontSize: 16, color: 'warning.main' }} />;
      case 'blocked':
        return <WarningIcon sx={{ fontSize: 16, color: 'warning.main' }} />;
    }
  };

  return (
    <Box sx={{ minWidth: 320, maxWidth: 380 }}>
      <Stack spacing={0}>
        {/* 1. Modality & Compliance */}
        <Box
          onClick={() => toggleSection('modality')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            cursor: editable ? 'pointer' : 'default',
            borderBottom: `1px solid ${theme.palette.divider}`,
            '&:hover': editable ? { bgcolor: 'action.hover' } : undefined,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: '50%',
              bgcolor: alpha(getStatusColor(getSectionStatus('modality')), 0.1),
              color: getStatusColor(getSectionStatus('modality')),
            }}
          >
            {getModalityIcon(modality)}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight={500}>
              {getModalityLabel(modality)} · POS {placeOfService}
            </Typography>
            {modality !== 'in_person' && telehealthConsent && (
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25 }}>
                <Chip
                  icon={<CheckIcon sx={{ fontSize: '12px !important' }} />}
                  label="Consent"
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              </Box>
            )}
          </Box>
          {getStatusIcon(getSectionStatus('modality'))}
          {editable && (
            <IconButton
              size="small"
              sx={{
                transform: expandedSection === 'modality' ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s',
              }}
            >
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Modality Editor (inline) */}
        <Collapse in={expandedSection === 'modality'}>
          <Box sx={{ p: 2, bgcolor: 'action.hover', borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Stack spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Modality</InputLabel>
                <Select
                  value={modality}
                  onChange={(e) => {
                    const val = e.target.value as 'in_person' | 'video' | 'audio';
                    handleModalityChange(val);
                  }}
                  label="Modality"
                >
                  <MenuItem value="in_person">In-person</MenuItem>
                  <MenuItem value="video">Video</MenuItem>
                  <MenuItem value="audio">Audio</MenuItem>
                </Select>
              </FormControl>
              {modality !== 'in_person' && (
                <>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={telehealthConsent}
                        onChange={(e) => handleAttestationChange('telehealth_consent', e.target.checked)}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Telehealth consent obtained</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={locationConfirmed}
                        onChange={(e) => handleAttestationChange('location_confirmed', e.target.checked)}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Location confirmed</Typography>}
                  />
                </>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button size="small" onClick={() => setExpandedSection(null)}>
                  Close
                </Button>
              </Box>
            </Stack>
          </Box>
        </Collapse>

        {/* 2. PDMP - Quick toggle section */}
        <Tooltip title={pdmpReviewed ? 'Click to uncheck PDMP' : 'Click to confirm PDMP reviewed'}>
          <Box
            onClick={handlePdmpQuickToggle}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1.5,
              cursor: editable ? 'pointer' : 'default',
              borderBottom: `1px solid ${theme.palette.divider}`,
              transition: 'background-color 0.2s',
              '&:hover': editable ? { bgcolor: 'action.hover' } : undefined,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: alpha(getStatusColor(getSectionStatus('pdmp')), 0.1),
                color: getStatusColor(getSectionStatus('pdmp')),
                transition: 'all 0.2s',
                '&:hover': editable
                  ? {
                      bgcolor: alpha(getStatusColor(getSectionStatus('pdmp')), 0.2),
                      transform: 'scale(1.1)',
                    }
                  : undefined,
              }}
            >
              <PdmpIcon sx={{ fontSize: 16 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={500}>
                PDMP Reviewed
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {pdmpReviewed ? 'Prescription monitoring verified' : 'Click to confirm'}
              </Typography>
            </Box>
            {pdmpReviewed ? (
              <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main', flexShrink: 0 }} />
            ) : (
              <UncheckedIcon sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }} />
            )}
            {/* Invisible IconButton to match alignment with other sections */}
            {editable && (
              <IconButton
                size="small"
                disabled
                sx={{
                  visibility: 'hidden',
                  pointerEvents: 'none',
                  flexShrink: 0,
                }}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Tooltip>

        {/* 3. Time */}
        <Box
          onClick={() => toggleSection('time')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            cursor: editable ? 'pointer' : 'default',
            borderBottom: `1px solid ${theme.palette.divider}`,
            '&:hover': editable ? { bgcolor: 'action.hover' } : undefined,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: '50%',
              bgcolor: alpha(getStatusColor(getSectionStatus('time')), 0.1),
              color: getStatusColor(getSectionStatus('time')),
            }}
          >
            <TimeIcon sx={{ fontSize: 16 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            {startTime ? (
              <>
                <Typography variant="body2" fontWeight={500}>
                  {format(startTime, 'MMM d · h:mm a')}
                  {endTime && <> – {format(endTime, 'h:mm a')}</>}
                </Typography>
                {durationDisplay && (
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25 }}>
                    <Chip label={durationDisplay} size="small" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }} />
                    {cptCodes.length === 2 && psychotherapyMinutes && (
                      <Chip
                        label={`Psych: ${psychotherapyMinutes}m`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                )}
              </>
            ) : (
              <Typography variant="body2" fontWeight={500}>
                No time recorded
              </Typography>
            )}
          </Box>
          {getStatusIcon(getSectionStatus('time'))}
          {editable && (
            <IconButton
              size="small"
              sx={{
                transform: expandedSection === 'time' ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s',
              }}
            >
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Time Editor (inline) */}
        <Collapse in={expandedSection === 'time'}>
          <Box sx={{ p: 2, bgcolor: 'action.hover', borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Stack spacing={2}>
              <TextField
                type="date"
                label="Date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  type="time"
                  label="Start Time"
                  value={selectedStartTime}
                  onChange={(e) => setSelectedStartTime(e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  disabled={!selectedDate}
                />
                <TextField
                  type="time"
                  label="End Time"
                  value={selectedEndTime}
                  onChange={(e) => setSelectedEndTime(e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  disabled={!selectedDate}
                />
              </Box>
              <TextField
                type="number"
                label="Total Minutes (optional - auto-calculated if end time set)"
                value={totalTimeMinutes}
                onChange={(e) => setTotalTimeMinutes(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: 0 }}
                disabled={!!selectedEndTime}
                helperText={selectedEndTime ? 'Duration calculated from start/end times' : 'Enter duration or set end time'}
              />
              {cptCodes.length === 2 && (
                <TextField
                  type="number"
                  label="Psychotherapy Minutes"
                  value={psychotherapyMinutes}
                  onChange={(e) => {
                    const val = e.target.value ? parseInt(e.target.value, 10) : null;
                    handleAttestationChange('psychotherapy_minutes', val);
                  }}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 0 }}
                />
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button size="small" onClick={() => setExpandedSection(null)}>
                  Close
                </Button>
              </Box>
            </Stack>
          </Box>
        </Collapse>

        {/* 4. Billing */}
        <Box
          onClick={() => toggleSection('billing')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            cursor: editable ? 'pointer' : 'default',
            borderBottom: `1px solid ${theme.palette.divider}`,
            '&:hover': editable ? { bgcolor: 'action.hover' } : undefined,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: '50%',
              bgcolor: alpha(getStatusColor(getSectionStatus('billing')), 0.1),
              color: getStatusColor(getSectionStatus('billing')),
            }}
          >
            <BillingIcon sx={{ fontSize: 16 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {cptCodes.length > 0 ? (
                cptCodes.map((code, idx) => (
                  <Chip
                    key={idx}
                    label={code}
                    size="small"
                    sx={{ height: 22, fontSize: '0.75rem', fontFamily: 'monospace', fontWeight: 600 }}
                  />
                ))
              ) : (
                <Typography variant="body2" sx={{ color: 'warning.main' }}>
                  Add CPT codes
                </Typography>
              )}
              {icd10Codes.length > 0 ? (
                icd10Codes.map((code, idx) => (
                  <Chip
                    key={code}
                    label={code}
                    size="small"
                    variant={idx === 0 ? 'filled' : 'outlined'}
                    color={idx === 0 ? 'primary' : 'default'}
                    sx={{ height: 22, fontSize: '0.75rem', fontFamily: 'monospace', fontWeight: idx === 0 ? 600 : 400 }}
                  />
                ))
              ) : (
                <Typography variant="body2" sx={{ color: 'warning.main' }}>
                  + ICD-10
                </Typography>
              )}
            </Box>
          </Box>
          {getStatusIcon(getSectionStatus('billing'))}
          {editable && (
            <IconButton
              size="small"
              sx={{
                transform: expandedSection === 'billing' ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s',
              }}
            >
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Billing Editor (inline) */}
        <Collapse in={expandedSection === 'billing'}>
          <Box sx={{ p: 2, bgcolor: 'action.hover', borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Stack spacing={2}>
              <CPTCodePicker
                value={cptCodes}
                onChange={(codes) => {
                  cptUserChangedRef.current = true;
                  setCptCodes(codes);
                }}
                showHeader={false}
              />
              <ICD10CodePicker
                value={icd10Codes}
                onChange={handleIcd10Change}
                showHeader={false}
                size="small"
              />
              {cptCodes.length === 2 && (
                <TextField
                  type="number"
                  label="Psychotherapy Minutes"
                  value={psychotherapyMinutes}
                  onChange={(e) => {
                    const val = e.target.value ? parseInt(e.target.value, 10) : null;
                    handleAttestationChange('psychotherapy_minutes', val);
                  }}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 0 }}
                />
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button size="small" onClick={() => setExpandedSection(null)}>
                  Close
                </Button>
              </Box>
            </Stack>
          </Box>
        </Collapse>

        {/* Sign/Complete Note Button with attestation text */}
        {onSignNote && (
          <Box sx={{ p: 2, pt: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Button
                variant="contained"
                onClick={onSignNote}
                disabled={isSigning}
                startIcon={isSigning ? undefined : <CheckIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  py: 1.5,
                  px: 3,
                  fontSize: '0.9375rem',
                  flex: '0 0 75%',
                }}
              >
                {isSigning
                  ? 'Processing...'
                  : hasNoteSigning && hasPatient
                    ? 'Sign Note'
                    : 'Complete Note'}
              </Button>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                  flex: '0 0 25%',
                  lineHeight: 1.4,
                }}
              >
                Note reviewed & accurate
              </Typography>
            </Box>
          </Box>
        )}
      </Stack>
    </Box>
  );
};
